(function () {
  // @MX:NOTE: [AUTO] Single source for the mobile/desktop split. Mirrors the
  // theme's CSS `@media (min-width: 769px)` boundary used across swatch styles.
  const DESKTOP_BREAKPOINT = 769;

  function getConfig() {
    return window.FoxTheme?.pcardSwatches || {};
  }

  function mergeBreakpoints(base, overrides) {
    if (!overrides) return base;
    const merged = { ...base };
    Object.keys(overrides).forEach((key) => {
      merged[key] = { ...merged[key], ...overrides[key] };
    });
    return merged;
  }

  function getSpaceBetweenValues() {
    const cfg = getConfig();
    const styles = getComputedStyle(document.documentElement);
    const cssMobile = parseFloat(styles.getPropertyValue('--pcard-swatch-spacing-mobile'));
    const cssDesktop = parseFloat(styles.getPropertyValue('--pcard-swatch-spacing-desktop'));
    const legacy = cfg.spaceBetween ?? 2;

    return {
      mobile: Number.isFinite(cssMobile)
        ? cssMobile
        : (cfg.spaceBetweenMobile ?? legacy),
      desktop: Number.isFinite(cssDesktop)
        ? cssDesktop
        : (cfg.spaceBetweenDesktop ?? legacy),
    };
  }

  // @MX:ANCHOR: [AUTO] DOM-measured chip width. With @container-query sizing,
  // chip diameter lives in the card container scope and is NOT readable from
  // :root, so fit/pagination math must measure the real rendered chip.
  // @MX:REASON: getComputedStyle(:root) reads global tokens that no longer
  // reflect the actual rendered size once `@container` drives `--pcard-swatch-size`.
  // Measuring `[data-swatches-slide] .swatch-color` keeps JS in sync with CSS.
  function measureSwatchEl(swiper) {
    const slides = swiper.el.querySelectorAll('[data-swatches-slide]');
    for (let i = 0; i < slides.length; i += 1) {
      const slide = slides[i];
      // The visible chip is `.swatch-color`; fall back to the slide itself.
      const chipEl = slide.querySelector('.swatch-color') || slide;
      const rect = chipEl.getBoundingClientRect();
      if (rect.width > 0) {
        return rect.width;
      }
    }
    return 0;
  }

  function getCssVarChipSize(swiper) {
    const styles = getComputedStyle(document.documentElement);
    const isDesktop = isDesktopSwatchBreakpoint(swiper);
    const size = parseFloat(
      styles.getPropertyValue(isDesktop ? '--pcard-swatch-size-desktop' : '--pcard-swatch-size-mobile')
    );

    return Number.isFinite(size) ? size : (isDesktop ? 26 : 23);
  }

  function getChipSize(swiper) {
    const measured = measureSwatchEl(swiper);
    if (measured > 0) {
      return measured;
    }
    // Slides not yet rendered/visible (e.g. hidden tab): use token fallback.
    return getCssVarChipSize(swiper);
  }

  // @MX:NOTE: [AUTO] Spacing is JS-controlled via the `spaceBetween` Swiper
  // param sourced from the global :root tokens (--pcard-swatch-spacing-*).
  // Do NOT measure the rendered gap here: the gap is an OUTPUT of this value,
  // so measuring it and feeding it back collapses spacing to 0 over iterations.
  // Only chip SIZE moved to @container scope and thus needs DOM measurement.
  function getActiveSpaceBetween(swiper) {
    const { mobile, desktop } = getSpaceBetweenValues();
    const currentBreakpoint =
      swiper.currentBreakpoint || Object.keys(swiper.params.breakpoints || {}).pop();
    const breakpointValue = Number(currentBreakpoint);

    if (Number.isFinite(breakpointValue) && breakpointValue < DESKTOP_BREAKPOINT) {
      return mobile;
    }

    const bpConfig = swiper.params.breakpoints?.[currentBreakpoint] || {};
    return bpConfig.spaceBetween ?? swiper.params.spaceBetween ?? desktop;
  }

  function isDesktopSwatchBreakpoint(swiper) {
    const currentBreakpoint =
      swiper.currentBreakpoint || Object.keys(swiper.params.breakpoints || {}).pop();
    const breakpointValue = Number(currentBreakpoint);

    if (Number.isFinite(breakpointValue)) {
      return breakpointValue >= DESKTOP_BREAKPOINT;
    }

    return window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`).matches;
  }

  function getVisibleCap(swiper) {
    const cfg = getConfig();
    const styles = getComputedStyle(document.documentElement);
    const cssMobile = parseInt(styles.getPropertyValue('--pcard-swatch-visible-mobile'), 10);
    const cssDesktop = parseInt(styles.getPropertyValue('--pcard-swatch-visible-desktop'), 10);
    const isDesktop = isDesktopSwatchBreakpoint(swiper);

    if (isDesktop) {
      return Number.isFinite(cssDesktop) ? cssDesktop : (cfg.desktopVisible ?? 6);
    }

    return Number.isFinite(cssMobile) ? cssMobile : (cfg.mobileVisible ?? 3);
  }

  function getMaxFittingChips(swiper) {
    const available = getAvailableSliderWidth(swiper, true);
    if (!available) return getVisibleCap(swiper);

    const spaceBetween = getActiveSpaceBetween(swiper);
    const chip = getChipSize(swiper);
    const unit = chip + spaceBetween;

    if (unit <= 0) return 1;

    return Math.max(1, Math.floor((available + spaceBetween) / unit));
  }

  function getVisibleChipCount(swiper, useCapOnly) {
    const cap = getVisibleCap(swiper);
    if (useCapOnly) return cap;
    return Math.min(cap, getMaxFittingChips(swiper));
  }

  function getPaginationThreshold() {
    const cfg = getConfig();
    const minForArrows = cfg.paginationCount;

    if (typeof minForArrows === 'number' && minForArrows > 0) {
      return minForArrows;
    }

    return null;
  }

  function getTrackWidth(swiper, viewportWidth) {
    const root = swiper.el.closest('.product-card__swatches');
    return viewportWidth + getArrowGutter(root);
  }

  function getVisibleViewportWidth(swiper, visibleCount) {
    const spaceBetween = getActiveSpaceBetween(swiper);
    const chip = getChipSize(swiper);
    const count = Math.max(1, visibleCount);

    return chip * count + Math.max(0, count - 1) * spaceBetween;
  }

  function applyVisibleViewport(swiper, paginate) {
    const track = swiper.el.closest('.product-card__swatches-track');

    if (!paginate) {
      track?.style.removeProperty('width');
      track?.style.removeProperty('max-width');
      swiper.el.style.removeProperty('width');
      swiper.el.style.removeProperty('max-width');
      return;
    }

    const visibleCount = getVisibleChipCount(swiper, true);
    const viewportWidth = getVisibleViewportWidth(swiper, visibleCount);
    const trackWidth = getTrackWidth(swiper, viewportWidth);

    swiper.el.style.width = `${viewportWidth}px`;
    swiper.el.style.maxWidth = `${viewportWidth}px`;

    if (track) {
      track.style.width = `${trackWidth}px`;
      track.style.maxWidth = '100%';
    }
  }

  function getArrowGutter(root) {
    const cfg = getConfig();
    const styles = getComputedStyle(root || document.documentElement);
    const arrowSize =
      cfg.arrowButtonSize ??
      (parseFloat(styles.getPropertyValue('--pcard-swatch-arrow-size')) || 20);
    const navGap =
      cfg.navGap ?? (parseFloat(styles.getPropertyValue('--pcard-swatch-nav-gap')) || 6);
    return arrowSize * 2 + navGap * 2;
  }

  function getSlidesContentWidth(swiper) {
    const slides = swiper.el.querySelectorAll('[data-swatches-slide]');
    if (!slides.length) return 0;

    const spaceBetween = getActiveSpaceBetween(swiper);
    const chip = getChipSize(swiper);
    const unit = chip + spaceBetween;

    return chip + Math.max(0, slides.length - 1) * unit;
  }

  function getAvailableSliderWidth(swiper, reserveNav) {
    const row = swiper.el.closest('.product-card__swatches-row');
    if (!row) return swiper.el.clientWidth || 0;

    const rowStyles = getComputedStyle(row);
    const rowGap = parseFloat(rowStyles.columnGap || rowStyles.gap || 0) || 0;
    const more = row.querySelector('.product-card__swatches-more');
    const moreWidth = more ? more.offsetWidth + rowGap : 0;
    const track = swiper.el.closest('.product-card__swatches-track');
    const trackGap = track ? parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || 0) || 0 : 0;
    const navWidth = reserveNav
      ? getArrowGutter(swiper.el.closest('.product-card__swatches')) + trackGap * 2
      : 0;

    return Math.max(0, row.clientWidth - moreWidth - navWidth);
  }

  function slidesWouldOverflow(swiper) {
    const available = getAvailableSliderWidth(swiper, true);
    if (!available) return false;

    return getSlidesContentWidth(swiper) > available + 1;
  }

  function getTotalSwatchCount(swiper) {
    const root = swiper.el.closest('.product-card__swatches');
    const total = parseInt(root?.dataset.swatchTotal, 10);
    const slides = swiper.el.querySelectorAll('[data-swatches-slide]');

    if (Number.isFinite(total) && total > 0) {
      return total;
    }

    return slides.length;
  }

  function getDomSwatchCap(swiper) {
    const root = swiper.el.closest('.product-card__swatches');
    const max = parseInt(root?.dataset.swatchMax, 10);

    if (Number.isFinite(max) && max > 0) {
      return max;
    }

    return getTotalSwatchCount(swiper);
  }

  function getEffectiveSwatchCount(swiper) {
    const total = getTotalSwatchCount(swiper);
    const domCap = getDomSwatchCap(swiper);
    const sliderCount = swiper.el.querySelectorAll('[data-swatches-slide]').length;

    return Math.min(total, domCap, sliderCount);
  }

  function shouldPaginate(swiper) {
    const cfg = getConfig();
    if (cfg.showNav === false) return false;

    const slides = swiper.el.querySelectorAll('[data-swatches-slide]');
    if (slides.length <= 1) return false;

    const effectiveTotal = getEffectiveSwatchCount(swiper);
    const threshold = getPaginationThreshold();

    if (threshold !== null) {
      return effectiveTotal > threshold;
    }

    const visibleCount = getVisibleChipCount(swiper, false);
    if (effectiveTotal > visibleCount) return true;

    return slidesWouldOverflow(swiper);
  }

  function fixNavButtonLayout(swiper) {
    const root = swiper.el.closest('.product-card__swatches');
    if (!root) return;

    root.querySelectorAll('[data-swiper-button-prev], [data-swiper-button-next]').forEach((btn) => {
      btn.style.position = '';
      btn.style.inset = '';
      btn.style.top = '';
      btn.style.right = '';
      btn.style.bottom = '';
      btn.style.left = '';
      btn.style.marginTop = '';
      btn.style.transform = '';
      btn.style.width = '';
      btn.style.height = '';
    });
  }

  function setNavEnabled(swiper, enabled) {
    const root = swiper.el.closest('.product-card__swatches');
    if (!root) return;

    root.querySelectorAll('[data-swiper-button-prev], [data-swiper-button-next]').forEach((btn) => {
      btn.toggleAttribute('hidden', !enabled);
      btn.setAttribute('aria-hidden', enabled ? 'false' : 'true');
    });

    if (enabled) {
      swiper.navigation?.enable?.();
    } else {
      swiper.navigation?.disable?.();
    }
  }

  function clearSlideInlineLayout(slides) {
    slides.forEach((slide) => {
      slide.style.removeProperty('width');
      slide.style.removeProperty('margin');
      slide.style.removeProperty('margin-right');
    });
  }

  function syncSwatchSliderParams(swiper) {
    const { mobile, desktop } = getSpaceBetweenValues();
    const spaceBetween = getActiveSpaceBetween(swiper);

    swiper.params.slidesPerView = 'auto';
    swiper.params.spaceBetween = spaceBetween;

    if (swiper.params.breakpoints) {
      Object.keys(swiper.params.breakpoints).forEach((key) => {
        const bp = swiper.params.breakpoints[key];
        swiper.params.breakpoints[key] = {
          ...bp,
          slidesPerView: 'auto',
          spaceBetween: Number(key) < DESKTOP_BREAKPOINT ? mobile : desktop,
        };
      });
    }

    clearSlideInlineLayout(swiper.el.querySelectorAll('[data-swatches-slide]'));
  }

  function applyPaginationState(swiper) {
    const root = swiper.el.closest('.product-card__swatches');
    const paginate = shouldPaginate(swiper);

    syncSwatchSliderParams(swiper);

    if (paginate) {
      swiper.el.classList.add('product-card__swatches--padding');
      root?.classList.add('product-card__swatches--style');
      setNavEnabled(swiper, true);
      applyVisibleViewport(swiper, true);
    } else {
      swiper.el.classList.remove('product-card__swatches--padding');
      root?.classList.remove('product-card__swatches--style');
      setNavEnabled(swiper, false);
      applyVisibleViewport(swiper, false);
      swiper.el.querySelectorAll('[data-swatches-slide]').forEach((element) => {
        element.style.width = 'max-content';
      });
    }

    swiper.update();
    if (paginate) {
      applyVisibleViewport(swiper, true);
    }
    fixNavButtonLayout(swiper);
  }

  function buildSliderOptions(el, overrides) {
    const cfg = getConfig();
    const { mobile: spaceBetweenMobile, desktop: spaceBetweenDesktop } = getSpaceBetweenValues();

    const breakpoints = mergeBreakpoints(
      {
        280: { slidesPerView: 'auto', spaceBetween: spaceBetweenMobile },
        374: { slidesPerView: 'auto', spaceBetween: spaceBetweenMobile },
        769: { slidesPerView: 'auto', spaceBetween: spaceBetweenDesktop },
        1024: { slidesPerView: 'auto', spaceBetween: spaceBetweenDesktop },
        1150: { slidesPerView: 'auto', spaceBetween: spaceBetweenDesktop },
        1400: { slidesPerView: 'auto', spaceBetween: spaceBetweenDesktop },
        1536: { slidesPerView: 'auto', spaceBetween: spaceBetweenDesktop },
        1610: { slidesPerView: 'auto', spaceBetween: spaceBetweenDesktop },
      },
      overrides?.breakpoints
    );

    const host = el.closest('.product-card__swatches') || el.parentElement;
    const paginationEl = cfg.dots ? host.querySelector('[data-swatches-pagination]') : null;

    const options = {
      slidesPerView: 'auto',
      slidesPerGroup: cfg.slidesPerGroup ?? 1,
      spaceBetween: spaceBetweenDesktop,
      speed: cfg.speed ?? 300,
      grabCursor: !!cfg.grabCursor,
      centeredSlides: !!cfg.centered,
      effect: cfg.effect === 'fade' ? 'fade' : 'slide',
      watchOverflow: true,
      navigation: {
        nextEl: host.querySelector('[data-swiper-button-next]'),
        prevEl: host.querySelector('[data-swiper-button-prev]'),
      },
      breakpoints,
      on: {
        init() {
          applyPaginationState(this);
        },
        resize() {
          applyPaginationState(this);
        },
        breakpoint() {
          applyPaginationState(this);
        },
      },
    };

    if (cfg.loop) {
      options.loop = true;
    } else if (cfg.rewind) {
      options.rewind = true;
    }

    if (cfg.effect === 'fade') {
      options.fadeEffect = { crossFade: true };
    }

    if (cfg.autoplay) {
      options.autoplay = {
        delay: cfg.autoplayDelay ?? 3000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      };
    }

    if (paginationEl) {
      options.pagination = {
        el: paginationEl,
        type: cfg.dotsType || 'bullets',
        clickable: true,
      };
    }

    return options;
  }

  function collectModules() {
    const cfg = getConfig();
    const S = window.FoxTheme.Swiper || {};
    const modules = [];
    if (cfg.autoplay && S.Autoplay) modules.push(S.Autoplay);
    if (cfg.effect === 'fade' && S.EffectFade) modules.push(S.EffectFade);
    return modules.length ? modules : null;
  }

  function initPcardSwatches(selector, overrides) {
    const elements = document.querySelectorAll(selector || '[data-swatches-slider]');
    const instances = [];

    elements.forEach((el) => {
      if (el.classList.contains('swiper-initialized')) return;

      const options = buildSliderOptions(el, overrides);
      const carousel = new window.FoxTheme.Carousel(el, options, collectModules());
      carousel.init();
      instances.push(carousel);
    });

    return instances;
  }

  function refreshPcardSwatches(root) {
    const scope = root || document;
    scope.querySelectorAll('[data-swatches-slider].swiper-initialized').forEach((el) => {
      if (el.swiper) {
        applyPaginationState(el.swiper);
        fixNavButtonLayout(el.swiper);
      }
    });
  }

  function syncPcardSwatchSpacingFromTheme() {
    refreshPcardSwatches();
  }

  window.FoxTheme = window.FoxTheme || {};
  window.FoxTheme.initPcardSwatches = initPcardSwatches;
  window.FoxTheme.refreshPcardSwatches = refreshPcardSwatches;
  window.FoxTheme.syncPcardSwatchSpacingFromTheme = syncPcardSwatchSpacingFromTheme;

  document.addEventListener('tabChange', () => {
    requestAnimationFrame(() => refreshPcardSwatches());
  });

  document.addEventListener('shopify:section:load', () => {
    requestAnimationFrame(() => refreshPcardSwatches());
  });

  window.addEventListener('resize', () => {
    refreshPcardSwatches();
  });
})();
